import { createContext, useContext, type ReactNode } from 'react'
import type { NumericString } from '@henlytics/types'

interface NumberFormatSettings {
  /** ISO 4217. 'XXX' means "no currency", shown until the farm's settings load. */
  currency: string
  /** BCP 47, e.g. 'en-IN'. Undefined falls back to the browser's own locale. */
  locale: string | undefined
}

/**
 * Currency and number format both come from the farm's settings (§11.1), so an
 * Indian farm reads ₹1,23,456.00 whatever language the laptop's browser is set to.
 */
const FormatContext = createContext<NumberFormatSettings>({ currency: 'XXX', locale: undefined })

export function CurrencyProvider({
  code,
  locale,
  children,
}: {
  code: string
  locale?: string
  children: ReactNode
}) {
  return <FormatContext.Provider value={{ currency: code, locale }}>{children}</FormatContext.Provider>
}

export function useCurrency(): string {
  return useContext(FormatContext).currency
}

export function useLocale(): string | undefined {
  return useContext(FormatContext).locale
}

export interface MoneyProps {
  value: NumericString | null | undefined
  /** Per-unit figures (cost per egg, per bird) need more than two decimals. */
  precision?: number
  /** Shown when the value is null — "—" by default, never a zero. */
  fallback?: string
}

/**
 * Display only.
 *
 * The string is parsed to a JS number purely to hand it to Intl for formatting;
 * the result is never read back, never stored, and never used in arithmetic.
 * All money math happens in `packages/costing` on exact decimals (§3.10).
 */
export function Money({ value, precision = 2, fallback = '—' }: MoneyProps) {
  const { currency, locale } = useContext(FormatContext)
  if (value === null || value === undefined) return <span className="muted">{fallback}</span>

  const asNumber = Number(value)
  if (Number.isNaN(asNumber)) return <span className="muted">{fallback}</span>

  const formatted =
    currency === 'XXX'
      ? asNumber.toLocaleString(locale, {
          minimumFractionDigits: precision,
          maximumFractionDigits: precision,
        })
      : asNumber.toLocaleString(locale, {
          style: 'currency',
          currency,
          minimumFractionDigits: precision,
          maximumFractionDigits: precision,
        })

  return (
    <span className="money" title={value}>
      {formatted}
    </span>
  )
}

/** A count of birds or eggs. Whole numbers, grouped the farm's way (1,23,456 in India). */
export function Count({ value, fallback = '—' }: { value: number | null | undefined; fallback?: string }) {
  const locale = useLocale()
  if (value === null || value === undefined) return <span className="muted">{fallback}</span>
  return <span className="count">{value.toLocaleString(locale)}</span>
}

export function Percent({ value, fallback = '—' }: { value: NumericString | null | undefined; fallback?: string }) {
  if (value === null || value === undefined) return <span className="muted">{fallback}</span>
  return <span className="pct">{Number(value).toFixed(1)}%</span>
}
